import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getLanguage } from "../../lang/helpers";
import { VisionStatementInterface } from "../DevelopmentPlan/interfaces";
import CustomButton from "../../components/CustomButton";
import RadioButton from "../../components/RadioButton";
import iconInfo from "url:shared-assets/icons/white/information-circle.svg";
import iconPencil from "url:shared-assets/icons/white/pencil.svg";
import Api from "../../services/Api";
import { StrArr, StrMap } from "../../interfaces/common";


interface Props {
  visionStatement: VisionStatementInterface;
  save: Function;
  setEdit: Function;
  showDetailModal: Function;
}

interface FeedbackQuestion {
  id: string;
  question_id?: string;
  name: string;
  translationsName?: Record<string, string>;
  scoreSelf?: number;
  scoreOther?: number;
  scoreOtherNormalized?: number;
  scoreSelfNormalized?: number;
  scoreWeighted?: number;
  scoreList?: number[];
  gap?: number;
  freeTexts?: string[];
  spreadRespondentsNormalized?: number;
}
interface QuestionInterface {
  questionId: string;
  names: StrMap;
  noAnswer: boolean;
  freeText: boolean;
  answer: number;
  freeAnswer: string;
}

interface SectionInterface {
  names: StrMap;
  questions: Array<QuestionInterface>;
}

interface SurveyInterface {
  language: string;
  languages: string[];
  deadline: string;
  answerOptions: number;
  answerType: number;
  showNoAnswer: boolean;
  translations_answers: StrArr;
  names: StrMap;
  sections: Array<SectionInterface>
}

const SelfAssessmentSurveyResponse = (props: Props) => {
  const { t, i18n } = useTranslation();
  const lang = getLanguage(i18n.language);
  const api = Api.getInstance();
  const [feedbackQuestions, setFeedbackQuestions] = useState<Array<FeedbackQuestion>>([]);
  const [feedbackAnswerOptions, setFeedbackAnswerOptions] = useState<string[]>([]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    const focusAreaQuestions =
      props.visionStatement.surveyRequests[0].surveyResult.focusAreas[0].questions;
    const answerOptionsByLang =
      props.visionStatement.surveyRequests[0].surveyResult.answerOptions;
    const _lang = props.visionStatement.surveyRequests[0].surveyResult.languages[0];

    setFeedbackQuestions(focusAreaQuestions);
    setFeedbackAnswerOptions(answerOptionsByLang[_lang]);
    setLoading(false);
  }, [props.visionStatement]);

  if (loading) {
    return <div className="p-8 text-gray-300">Loading...</div>;
  }

  const allQuestionsAnswered = () => {
    return feedbackQuestions.every((_, index) => answers[index] !== undefined && answers[index] !== "");
  };

  const sendResponses = () => {
    const surveyRequest = props.visionStatement.surveyRequests[0];
    const surveyResult = surveyRequest.surveyResult;

    const questions: QuestionInterface[] = feedbackQuestions.map((question, index) => {
      const selectedOption = answers[index];
      const answerIndex = feedbackAnswerOptions.indexOf(selectedOption);

      return {
        questionId: question.question_id ?? question.id,
        names: { [lang]: question.name },
        noAnswer: false,
        freeText: false,
        answer: answerIndex,
        freeAnswer: "",
      };
    });

    const updatedSurvey: SurveyInterface = {
      language: lang,
      languages: surveyResult.languages,
      deadline: surveyRequest.deadline,
      answerOptions: surveyResult.nrAnswerOptions,
      answerType: 0,
      showNoAnswer: true,
      names: { [lang]: props.visionStatement.title },
      translations_answers: surveyResult.answerOptions,
      sections: [
        {
          names: { [lang]: "" },
          questions,
        },
      ],
    };

    api.post('client/survey/' + props.visionStatement.surveyRequests[0].id, updatedSurvey).then((response) => {
      if (response.json.status === "ok") {
        props.save();
      } else {
        alert("Error submitting responses");
      }
    }).catch((err) => {
      console.error("sendResponses error:", err);
      alert("Network error submitting responses");
    });
  }

  const colWidth = 100 / (feedbackAnswerOptions.length - 1) + "%";

  return (
    <div className="main-content m-4">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h1 className="z-heading1 text-white">
              {t("self-assessment")}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <CustomButton
              buttonType="transparentBase"
              size="sm"
              onClick={() => props.showDetailModal()}
              leftIcon={<img src={iconInfo} className="h-4 w-4" alt="info" />}
              style={{ width: "unset" }}
            >
              {t("long-term-goal-details")}
            </CustomButton>
            <CustomButton
              buttonType="transparentBase"
              size="sm"
              onClick={() => props.setEdit()}
              leftIcon={<img src={iconPencil} className="h-4 w-4" alt="edit" />}
              style={{ width: "unset" }}
            >
              {t("btn-edit-questions")}
            </CustomButton>
          </div>
        </div>
      </div>
      <div className="mb-6">
        {t('surveyIntro')}
      </div>
      <hr className="mt-1 border-indigo-700 mb-6" />
      <div>
        {feedbackQuestions.map((question, index) => (
          <div key={"feedback_question_" + index} className="mb-6">
            <div className="z-badge transparent text-gray-600">
              {t("question")} {index + 1}
            </div>
            <p className="text-gray-400 mt-2">{question.name}</p>

            <div className="mt-2 flex justify-between items-end gap-x-4 border-b border-indigo-700 pb-1">
              {feedbackAnswerOptions.filter((a, i) => i > 0).map((option, optIndex) => (
                <div key={"fqt" + index + "_opt" + optIndex} className="text-center" style={{width: colWidth}}>{option}</div>
              ))}
            </div>
            <div className="mt-4 flex justify-between gap-4 border-b border-indigo-700 pb-4">
              {feedbackAnswerOptions.filter((a, i) => i > 0).map((option, optIndex) => (
                <div className="flex justify-center" key={"fq" + index + "_opt" + optIndex} style={{width: colWidth}}>
                <RadioButton
                  id={"feedback_question_" + index + "_opt_" + optIndex}
                  name={"feedback_question_" + index}
                  value={option}
                  label=""
                  checked={answers[index] === option}
                  onChange={() => setAnswers((prev) => ({ ...prev, [index]: option }))}
                />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <CustomButton
            buttonType="blueBase"
            onClick={() => sendResponses()}
            style={{ width: "unset" }}
            disabled={!allQuestionsAnswered()}
        >
          {t("submit")}
        </CustomButton>
      </div>

    </div>
  );
};

export default SelfAssessmentSurveyResponse;